import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Dropdown, { DropdownItemProps, DropdownProps } from '@ui/Dropdown';
import { StyledInput, StyledInputProps } from '@ui/Form';
import React, { CSSProperties, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { cva } from 'class-variance-authority';
import { cn } from '~/lib/cn';
import { useInputError } from './hooks';

export type Option = {
    key: string | number;
    label: string;
    color?: DropdownItemProps['color'];
    icon?: IconDefinition;
};

interface Props {
    /** @deprecated  */
    css?: any;
    /** @deprecated  */
    dropdownCss?: CSSProperties;
    className?: string;
    name: string;
    label?: string | React.ReactNode;
    description?: string | React.ReactNode;
    options: Option[];
    buttonProps?: Partial<DropdownProps>;
    customOption?: boolean;
    customOptionProps?: Partial<Omit<StyledInputProps, 'name'>>;
    disableCustomValue?: boolean;
    /** @deprecated */
    defaultValue?: string | number;
    size?: 'sm' | 'md' | 'full';
}

/** Description styling below a form control; also used by FormMappingRow so both look identical. */
export const descriptionClassName = 'tw-ml-2 tw-mt-1 tw-text-[11px] tw-text-accents-6';

/** Label styling of a form control; also used by FormMappingRow so both look identical. */
export const labelClassNames = cva('tw-block tw-font-normal tw-text-sm tw-mb-1 tw-pl-1', {
    variants: {
        isInvalid: {
            true: 'tw-text-[#F31260]',
            false: 'tw-text-accents-9'
        }
    },
    defaultVariants: {
        isInvalid: false
    }
});

export const FormSelect = ({
    name,
    label,
    description,
    options: _options = [],
    customOption = false,
    customOptionProps,
    buttonProps,
    disableCustomValue,
    className
}: Props) => {
    const { t } = useTranslation('form');
    const { control, getValues } = useFormContext();
    const options = [..._options, ...(customOption ? [{ key: '_custom', label: t('select.customValue') }] : [])];
    const getValue = (value) => (Array.isArray(value) ? value?.[0] : value);
    const getObjForValue = (value?) => options.find((o) => o.key == getValue(value || getValues(name)));
    const [isCustomValue, setIsCustomValue] = useState(getValues(name) && getObjForValue() === undefined);
    const { message: errorMessage } = useInputError(name);

    useEffect(() => {
        if (!disableCustomValue)
            setIsCustomValue(
                getValues(name) !== undefined && getValues(name) !== null && getObjForValue() === undefined
            );
    }, [_options]);

    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { value, onChange, onBlur, disabled } }) => {
                const {
                    className: dropdownButtonClassName,
                    containerClassName,
                    disabled: buttonDisabled,
                    isDisabled: buttonIsDisabled,
                    color: buttonColor,
                    flat,
                    block,
                    label: placeholderOverride,
                    ...restButtonProps
                } = buttonProps || {};

                const selectedOption = getObjForValue(value);
                const hasOptions = options.length > 0;
                const placeholder = placeholderOverride ?? t('select.placeholder');
                const dropdownLabel = !hasOptions
                    ? t('select.empty')
                    : !disableCustomValue && isCustomValue
                      ? t('select.customValue')
                      : selectedOption?.label || placeholder;
                const dropdownDisabled = !hasOptions || buttonDisabled || buttonIsDisabled || disabled;

                return (
                    <div className={cn('tw-my-3 tw-w-full', className)}>
                        {label && <label className={labelClassNames({ isInvalid: !!errorMessage })}>{label}</label>}
                        <Dropdown
                            {...restButtonProps}
                            label={dropdownLabel}
                            block={block ?? true}
                            onBlur={onBlur}
                            flat={flat ?? true}
                            color={errorMessage ? 'error' : (buttonColor ?? 'light')}
                            disabled={dropdownDisabled}
                            isDisabled={dropdownDisabled}
                            className={dropdownButtonClassName}
                            placement="bottom"
                            size="sm"
                            containerClassName={cn('tw-w-full', containerClassName)}
                        >
                            {hasOptions &&
                                options.map((item) => (
                                    <Dropdown.Item
                                        key={item.key}
                                        label={item.label}
                                        color={item.color}
                                        icon={item.icon ? <FontAwesomeIcon icon={item.icon} /> : undefined}
                                        onClick={() => {
                                            setIsCustomValue(item.key === '_custom');
                                            onChange(item.key === '_custom' ? '' : item.key);
                                        }}
                                    />
                                ))}
                        </Dropdown>
                        {!disableCustomValue && isCustomValue && (
                            <StyledInput
                                name={name}
                                placeholder="Eigenen Wert eingeben"
                                className="tw-mt-6"
                                {...customOptionProps}
                                value={value}
                                onChange={(e) => onChange(e.currentTarget.value)}
                            />
                        )}
                        {errorMessage && (
                            <p className="tw-ml-2 tw-mt-1 tw-text-[10px] tw-font-medium tw-text-error">
                                {errorMessage}
                            </p>
                        )}
                        {description && <p className={descriptionClassName}>{description}</p>}
                    </div>
                );
            }}
        />
    );
};
